import { ChevronDown, ChevronRight, FilePlus, FileText, Pencil } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { Link } from 'react-router'
import useSWR from 'swr'

import { DocumentSheet } from '@/components/documents/DocumentSheet'
import {
  displayStatus,
  Doc,
  docCurrency,
  DocKind,
  docTotal,
  formatShortDate,
  relativeDue,
  statusGroup,
  statusLabelFor,
} from '@/components/documents/documentUtils'
import { Money, SegmentedTabs, StatStrip, StatusBadge } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import { InitialsAvatar } from '@/components/ParticipantAvatar'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiFetcher } from '@/data/fetcher'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import { Participant } from '@/interfaces/Participant'
import { Settings } from '@/interfaces/Settings'
import { cn } from '@/lib/utils'
import { components } from '@invo.space/shared'
import { Role } from '@/types/User'

import { balanceOf, currencyEntries, useCountryName } from './contactUtils'
import { VatPayerReliability } from './VatPayerReliability'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

const PAGE = 15

interface ContactDetailProps {
  contact: Participant | undefined
  isLoading?: boolean
}

const byIssuedDesc = (a: Doc, b: Doc) => (b.issuedAt ?? '').localeCompare(a.issuedAt ?? '')

/** Částky ve více měnách pod sebou, hlavní měna první. */
const Amounts = ({ amounts, primary, className }: { amounts: Record<string, number>; primary: string; className?: string }) => {
  const entries = currencyEntries(amounts, primary)
  if (!entries.length) return <Money amount={0} currency={primary} size="lg" className={className} />
  return (
    <span className="flex flex-col">
      {entries.map(([currency, amount], i) => (
        <Money key={currency} amount={amount} currency={currency} size={i === 0 ? 'lg' : 'sm'} className={className} />
      ))}
    </span>
  )
}

/** Blok údajů v levém sloupci (Stripe customer). */
const DetailSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-3 border-t pt-5 first:border-t-0 first:pt-0">
    <h2 className="text-xs font-medium text-muted-foreground">{title}</h2>
    <dl className="space-y-3">{children}</dl>
  </section>
)

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-0.5">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm break-words">{children || <span className="text-muted-foreground">-</span>}</dd>
  </div>
)

export const ContactDetail = ({ contact }: ContactDetailProps) => {
  const canEdit = useRoleCheck([Role.owner, Role.manager, Role.employee])
  const countryName = useCountryName()
  const [tabChoice, setTab] = useState<DocKind | null>(null)
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<{ kind: DocKind; doc: Doc } | null>(null)

  const id = contact?.id
  const year = new Date().getFullYear()

  const { data: settings } = useSWR<Settings>('/api/v2/settings', apiFetcher)
  const primary = settings?.primaryCurrency ?? 'CZK'

  const invoicesSWR = useSWR<Doc[]>(id ? `/api/v2/invoices?customerId=${id}` : null, apiFetcher)
  const ordersSWR = useSWR<Doc[]>(id ? `/api/v2/orders?customerId=${id}` : null, apiFetcher)
  // API neumí filtrovat náklady podle dodavatele, bereme letošní a loňský rok (sdílená cache se seznamem nákladů)
  const expensesThisYear = useSWR<Doc[]>(id ? `/api/v2/expenses?year=${year}&limit=1000` : null, apiFetcher)
  const expensesLastYear = useSWR<Doc[]>(id ? `/api/v2/expenses?year=${year - 1}&limit=1000` : null, apiFetcher)

  const { data: bankAccountsData } = useSWR<{ data?: InvoicingBankAccount[] }>(
    contact?.defaultBankAccountId ? '/api/v2/bank-accounts' : null,
    apiFetcher,
  )
  const defaultBankAccount = bankAccountsData?.data?.find((a) => a.id === contact?.defaultBankAccountId)

  // filtrujeme i na klientu: endpoint výzev parametr customerId ignoruje
  const invoices = useMemo(
    () => (invoicesSWR.data ?? []).filter((d) => d.customer?.id === id).sort(byIssuedDesc),
    [invoicesSWR.data, id],
  )
  const orders = useMemo(
    () => (ordersSWR.data ?? []).filter((d) => d.customer?.id === id).sort(byIssuedDesc),
    [ordersSWR.data, id],
  )
  const expenses = useMemo(
    () =>
      [...(expensesThisYear.data ?? []), ...(expensesLastYear.data ?? [])]
        .filter((d) => d.supplier?.id === id)
        .sort(byIssuedDesc),
    [expensesThisYear.data, expensesLastYear.data, id],
  )

  const receivable = useMemo(() => balanceOf(invoices), [invoices])
  const payable = useMemo(() => balanceOf(expenses), [expenses])

  const lists: Record<DocKind, { docs: Doc[]; loading: boolean; refresh: () => Promise<unknown> }> = {
    invoice: { docs: invoices, loading: invoicesSWR.isLoading, refresh: () => invoicesSWR.mutate() },
    order: { docs: orders, loading: ordersSWR.isLoading, refresh: () => ordersSWR.mutate() },
    expense: {
      docs: expenses,
      loading: expensesThisYear.isLoading || expensesLastYear.isLoading,
      refresh: () => Promise.all([expensesThisYear.mutate(), expensesLastYear.mutate()]),
    },
  }

  const allLoaded = !invoicesSWR.isLoading && !ordersSWR.isLoading && !lists.expense.loading
  const isSupplierOnly = allLoaded && !invoices.length && !orders.length && expenses.length > 0
  // u čistého dodavatele otevřeme rovnou náklady
  // výchozí záložka = první, kde něco je (faktury, výzvy, náklady)
  const firstNonEmpty: DocKind = !allLoaded || invoices.length ? 'invoice' : orders.length ? 'order' : expenses.length ? 'expense' : 'invoice'
  const tab: DocKind = tabChoice ?? (isSupplierOnly ? 'expense' : firstNonEmpty)
  const current = lists[tab]
  const pageDocs = current.docs.slice(page * PAGE, (page + 1) * PAGE)
  const pages = Math.max(1, Math.ceil(current.docs.length / PAGE))
  const summaryLoading = invoicesSWR.isLoading

  if (!contact) return null

  const address = contact.address
  const cityLine = [address?.zip, address?.city].filter(Boolean).join(' ')

  const docCount = (n: number, kind: 'invoice' | 'expense') =>
    kind === 'invoice'
      ? `${n} ${n === 1 ? 'faktura' : n >= 2 && n <= 4 ? 'faktury' : 'faktur'}`
      : `${n} ${n === 1 ? 'náklad' : n >= 2 && n <= 4 ? 'náklady' : 'nákladů'}`
  const loadingValue = <Skeleton className="h-8 w-32" />

  const receivableStats = [
    {
      label: 'Neuhrazeno',
      value: summaryLoading ? loadingValue : <Amounts amounts={receivable.open} primary={primary} />,
      hint: summaryLoading ? undefined : docCount(receivable.openCount, 'invoice'),
    },
    {
      label: 'Po splatnosti',
      value: summaryLoading ? (
        loadingValue
      ) : (
        <Amounts amounts={receivable.overdue} primary={primary} className={receivable.overdueCount ? 'text-danger' : ''} />
      ),
      hint: summaryLoading ? undefined : receivable.overdueCount ? docCount(receivable.overdueCount, 'invoice') : 'Vše v termínu',
    },
    {
      label: 'Fakturováno celkem',
      value: summaryLoading ? loadingValue : <Amounts amounts={receivable.billed} primary={primary} />,
      hint: summaryLoading ? undefined : `${docCount(invoices.length, 'invoice')} celkem`,
    },
  ]

  const payableStat = {
    label: 'Dlužíme dodavateli',
    value: <Amounts amounts={payable.open} primary={primary} className={payable.overdueCount ? 'text-danger' : ''} />,
    hint: payable.openCount
      ? `${docCount(payable.openCount, 'expense')} k úhradě${payable.overdueCount ? `, ${payable.overdueCount} po splatnosti` : ''}`
      : 'Náklady uhrazeny',
  }

  const stats = isSupplierOnly
    ? [
        payableStat,
        {
          label: 'Náklady celkem',
          value: <Amounts amounts={payable.billed} primary={primary} />,
          hint: `${docCount(expenses.length, 'expense')} za roky ${year - 1} a ${year}`,
        },
      ]
    : [...receivableStats, ...(expenses.length ? [payableStat] : [])]

  const counterpartyColumn = tab === 'expense' ? 'Popis' : null

  return (
    <div className="mx-auto w-full max-w-[1280px]">
      {/* Hlavička */}
      <div className="flex flex-wrap items-start justify-between gap-4 pb-6">
        <div className="flex min-w-0 items-center gap-4">
          <InitialsAvatar name={contact.name} className="size-12 text-base" />
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-medium tracking-[-0.02em]">{contact.name}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground tabular">
              {[contact.ico && `IČO ${contact.ico}`, address?.city, isSupplierOnly ? 'dodavatel' : null].filter(Boolean).join(', ') ||
                'Kontakt'}
            </p>
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to={`/contact/${contact.id}/edit`}>
                <Pencil />
                Upravit
              </Link>
            </Button>
            <DropdownMenu>
              <ButtonGroup>
                <Button asChild>
                  <Link to={`/invoice/new?customerId=${contact.id}`}>
                    <FilePlus />
                    Vystavit fakturu
                  </Link>
                </Button>
                <DropdownMenuTrigger asChild>
                  <Button className="px-2" aria-label="Další doklady">
                    <ChevronDown />
                  </Button>
                </DropdownMenuTrigger>
              </ButtonGroup>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to={`/order/new?customerId=${contact.id}`}>
                    <FilePlus />
                    Vystavit výzvu k platbě
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <StatStrip className="mb-8" items={stats} />

      <div className="grid gap-8 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-10">
        {/* Levý sloupec: údaje */}
        <aside className="space-y-5">
          <DetailSection title="Firma">
            <DetailRow label="IČO">{contact.ico && <span className="tabular">{contact.ico}</span>}</DetailRow>
            <DetailRow label="DIČ">{contact.dic && <span className="tabular">{contact.dic}</span>}</DetailRow>
            <DetailRow label="DPH">
              {contact.vatPayer === 'vatPayer' ? (
                <span className="flex flex-wrap items-center gap-2">
                  Plátce DPH
                  <VatPayerReliability participant={contact} />
                </span>
              ) : contact.vatPayer === 'noVatPayer' ? (
                'Neplátce DPH'
              ) : contact.vatPayer ? (
                'Identifikovaná osoba'
              ) : null}
            </DetailRow>
          </DetailSection>

          <DetailSection title="Kontakt">
            <DetailRow label="E-mail">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="text-link hover:underline">
                  {contact.email}
                </a>
              )}
            </DetailRow>
            <DetailRow label="Telefon">
              {contact.phone && (
                <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="tabular hover:underline">
                  {contact.phone}
                </a>
              )}
            </DetailRow>
          </DetailSection>

          <DetailSection title="Adresa">
            <DetailRow label="Sídlo">
              {(address?.address || cityLine || address?.country) && (
                <address className="not-italic">
                  {address?.address && <span className="block">{address.address}</span>}
                  {cityLine && <span className="block tabular">{cityLine}</span>}
                  {address?.country && <span className="block text-muted-foreground">{countryName(address.country)}</span>}
                </address>
              )}
            </DetailRow>
          </DetailSection>

          <DetailSection title="Bankovní účet">
            <DetailRow label="Číslo účtu">{contact.accountNumber && <span className="tabular">{contact.accountNumber}</span>}</DetailRow>
            <DetailRow label="IBAN">{contact.iban && <span className="tabular">{contact.iban}</span>}</DetailRow>
            {defaultBankAccount && (
              <DetailRow label="Výchozí účet na fakturách">{defaultBankAccount.name}</DetailRow>
            )}
          </DetailSection>
        </aside>

        {/* Pravý sloupec: doklady */}
        <section className="min-w-0">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <SegmentedTabs<DocKind>
              value={tab}
              onChange={(v) => {
                setTab(v)
                setPage(0)
              }}
              options={[
                { value: 'invoice', label: 'Faktury', count: invoicesSWR.isLoading ? undefined : invoices.length },
                { value: 'order', label: 'Výzvy k platbě', count: ordersSWR.isLoading ? undefined : orders.length },
                { value: 'expense', label: 'Náklady', count: lists.expense.loading ? undefined : expenses.length },
              ]}
            />
            {tab === 'expense' && <span className="text-xs text-muted-foreground">Za roky {year - 1} a {year}</span>}
          </div>

          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="md:w-36">Číslo</TableHead>
                  {counterpartyColumn && <TableHead className="hidden md:table-cell">{counterpartyColumn}</TableHead>}
                  <TableHead className={cn('hidden md:table-cell', !counterpartyColumn && 'w-auto')}>Splatnost</TableHead>
                  <TableHead className="text-right md:w-36">Částka</TableHead>
                  <TableHead className="hidden w-36 md:table-cell">Stav</TableHead>
                  <TableHead className="hidden w-8 md:table-cell" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {current.loading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: counterpartyColumn ? 6 : 5 }).map((__, j) => (
                        <TableCell key={j} className={cn(j !== 0 && j !== (counterpartyColumn ? 3 : 2) && 'hidden md:table-cell')}><Skeleton className="h-4 w-full max-w-24" /></TableCell>
                      ))}
                    </TableRow>
                  ))}
                {!current.loading &&
                  pageDocs.map((d) => {
                    const st = displayStatus(d)
                    const group = statusGroup(d)
                    const due = group === 'paid' || group === 'cancelled' ? null : relativeDue(d.deadlineAt)
                    return (
                      <TableRow key={d.uuid} onClick={() => setSelected({ kind: tab, doc: d })} className="group cursor-pointer">
                        <TableCell className="w-full max-w-0 md:w-auto md:max-w-none">
                          <p className="truncate font-medium tabular md:overflow-visible">{d.symbol}</p>
                          <p className="hidden text-xs text-muted-foreground tabular md:block">{formatShortDate(d.issuedAt)}</p>
                          <p className="truncate text-xs text-muted-foreground tabular md:hidden">
                            {formatShortDate(d.issuedAt)}
                            {due && <span className={cn(due.late && 'text-danger')}>, {due.text}</span>}
                          </p>
                        </TableCell>
                        {counterpartyColumn && (
                          <TableCell className="hidden max-w-0 md:table-cell">
                            <p className="truncate text-muted-foreground">{d.description || d.items?.[0]?.description || '-'}</p>
                          </TableCell>
                        )}
                        <TableCell className="hidden md:table-cell">
                          <p className="tabular">{formatShortDate(d.deadlineAt)}</p>
                          {due && <p className={cn('text-xs', due.late ? 'text-danger' : 'text-muted-foreground')}>{due.text}</p>}
                          {group === 'paid' && d.paidAt && (
                            <p className="text-xs text-muted-foreground">zaplaceno {formatShortDate(d.paidAt)}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Money amount={docTotal(d)} currency={docCurrency(d)} />
                          <div className="mt-0.5 md:hidden">
                            <StatusBadge status={st} label={statusLabelFor(tab, st)} />
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <StatusBadge status={st} label={statusLabelFor(tab, st)} />
                        </TableCell>
                        <TableCell className="hidden text-right md:table-cell">
                          <ChevronRight className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>

            {!current.loading && current.docs.length === 0 && (
              <div className="p-6">
                <EmptyState icon={FileText}
                  title={
                    tab === 'invoice'
                      ? 'Kontaktu jste zatím nevystavili fakturu'
                      : tab === 'order'
                        ? 'Žádné výzvy k platbě'
                        : 'Od tohoto dodavatele nemáte náklady'
                  }
                  ctaButton={
                    canEdit && tab !== 'expense' ? (
                      <Button variant="outline" asChild>
                        <Link to={`/${tab}/new?customerId=${contact.id}`}>
                          {tab === 'invoice' ? 'Vystavit fakturu' : 'Vystavit výzvu k platbě'}
                        </Link>
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            )}

            {current.docs.length > PAGE && (
              <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
                <span className="tabular">
                  {page * PAGE + 1}-{Math.min((page + 1) * PAGE, current.docs.length)} z {current.docs.length}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Předchozí
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
                    Další
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <DocumentSheet
        kind={selected?.kind ?? 'invoice'}
        doc={selected?.doc ?? null}
        onOpenChange={(o) => !o && setSelected(null)}
        onChanged={async () => {
          if (!selected) return
          await lists[selected.kind].refresh()
          setSelected(null)
        }}
      />
    </div>
  )
}
